import React from 'react';
import Logo from './Logo';

/* ══════════════════════════════════════════════════════════════════════════
   FOOTER — RevUpAI
   - Social: LinkedIn, X (Twitter), GitHub
   - Email: sales@revupai.com
   - Mobile responsive (stacks to 2-col then 1-col)
   ══════════════════════════════════════════════════════════════════════════ */

const LINKS = {
  'AI Roles': [
    { label:'RevSec — AI CISO',           href:'/ai-ciso' },
    { label:'RevModernize — AI Architect', href:'/ai-architect' },
    { label:'RevMigrate — AI DBA',        href:'/ai-dba' },
    { label:'RevTest — AI QA',            href:'/ai-qa' },
    { label:'Custom AI Role',             href:'/custom' },
  ],
  'Company': [
    { label: 'About',       href: '/about' },
    { label: 'Pricing',     href: '/pricing' },
    { label: 'Blog',        href: '/blog' },
    { label: 'Contact',     href: '/contact' },
    { label: 'Book a Demo', href: '/contact' },
  ],
  'Legal': [
    { label:'Privacy Policy',   href:'/privacy' },
    { label:'Terms of Service', href:'/terms' },
    { label:'Security',         href:'/security' },
  ],
};

/* Social icons */
const SOCIALS = [
  {
    label:'LinkedIn',
    href: 'https://www.linkedin.com/company/rev-up-ai/',
    icon:(
      <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
        <rect x="2" y="9" width="4" height="12"/>
        <circle cx="4" cy="4" r="2"/>
      </svg>
    ),
  },
  {
    label:'X / Twitter',
    href:'https://x.com/revupai',
    icon:(
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
  },
  {
    label:'GitHub',
    href:'https://github.com/revupai',
    icon:(
      <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
        <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
      </svg>
    ),
  },
];

export default function Footer() {
  return (
    <footer aria-label="RevUpAI site footer" style={{ borderTop:'1px solid rgba(0,0,0,.08)', padding:'56px clamp(24px,5vw,100px) 36px', background:'rgba(237,234,246,.97)' }}>
      <div style={{ maxWidth:1800, margin:'0 auto' }}>

        {/* ── Main grid ── */}
        <div style={{ display:'grid', gridTemplateColumns:'minmax(220px,2fr) repeat(3,1fr)', gap:48, marginBottom:48 }}>

          {/* Brand column */}
          <div>
            <Logo/>
            <p style={{ fontSize:13.5, color:'#4A3F6B', lineHeight:1.75, marginTop:16, maxWidth:280, fontFamily:"'DM Sans',system-ui,sans-serif" }}>
              Operated AI roles for the enterprise. AI CISO, AI Architect, AI DBA and AI QA — deployed and running from day one.
            </p>

            {/* Operated AI badge */}
            <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:20, padding:'6px 12px 6px 8px', borderRadius:100, background:'rgba(0,212,170,.07)', border:'1px solid rgba(0,212,170,.22)', width:'fit-content' }}>
              <div aria-hidden="true" style={{ width:6, height:6, borderRadius:'50%', background:'#00D4AA' }}/>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'rgba(0,180,140,.85)', fontFamily:"'DM Sans',system-ui,sans-serif" }}>Operated AI</span>
            </div>

            {/* Email */}
            <a href="mailto:sales@revupai.com" style={{ display:'inline-flex', alignItems:'center', gap:8, marginTop:20, textDecoration:'none', color:'#1A0A2E', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:500, padding:'8px 14px', borderRadius:8, background:'rgba(0,212,170,.07)', border:'1px solid rgba(0,212,170,.2)', transition:'all .2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.background='rgba(0,212,170,.13)'; e.currentTarget.style.borderColor='rgba(0,212,170,.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.background='rgba(0,212,170,.07)'; e.currentTarget.style.borderColor='rgba(0,212,170,.2)'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00A882" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              sales@revupai.com
            </a>

            {/* Social icons */}
            <div style={{ display:'flex', gap:8, marginTop:16 }}>
              {SOCIALS.map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', width:36, height:36, borderRadius:9, background:'rgba(0,0,0,.05)', border:'1px solid rgba(0,0,0,.1)', color:'#4A3F6B', textDecoration:'none', transition:'all .2s ease' }}
                  onMouseEnter={e => { e.currentTarget.style.background='rgba(124,58,237,.1)'; e.currentTarget.style.borderColor='rgba(124,58,237,.3)'; e.currentTarget.style.color='#7C3AED'; }}
                  onMouseLeave={e => { e.currentTarget.style.background='rgba(0,0,0,.05)'; e.currentTarget.style.borderColor='rgba(0,0,0,.1)'; e.currentTarget.style.color='#4A3F6B'; }}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Nav columns */}
          {Object.entries(LINKS).map(([heading, items]) => (
            <nav key={heading} aria-label={`${heading} links`}>
              <p style={{ fontSize:11, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:'#7C3AED', margin:'0 0 14px', fontFamily:"'DM Sans',system-ui,sans-serif" }}>
                {heading}
              </p>
              <ul style={{ listStyle:'none', padding:0, margin:0, display:'flex', flexDirection:'column', gap:10 }}>
                {items.map(({ label, href }) => (
                  <li key={label}>
                    <a href={href} style={{ fontSize:13.5, color:'#4A3F6B', fontFamily:"'DM Sans',system-ui,sans-serif", textDecoration:'none', transition:'color .2s', display:'block' }}
                      onMouseEnter={e => e.currentTarget.style.color='#1A0A2E'}
                      onMouseLeave={e => e.currentTarget.style.color='#4A3F6B'}
                    >{label}</a>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* ── Bottom bar ── */}
        <div style={{ paddingTop:24, borderTop:'1px solid rgba(0,0,0,.07)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
          <span style={{ fontSize:12.5, color:'#4A3F6B', fontFamily:"'DM Sans',system-ui,sans-serif" }}>
            © 2026 RevUpAI. All rights reserved.
          </span>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            {/* Inline contact nudge */}
            <a href="mailto:sales@revupai.com" style={{ fontSize:12.5, color:'#00A882', fontFamily:"'DM Sans',system-ui,sans-serif", textDecoration:'none', fontWeight:500 }}
              onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
              onMouseLeave={e => e.currentTarget.style.textDecoration='none'}
            >sales@revupai.com</a>
            <span style={{ fontSize:12.5, color:'#9B8FC4', fontFamily:"'DM Sans',system-ui,sans-serif" }}>Operated AI™</span>
          </div>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 900px) {
          footer > div > div:first-child {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        @media (max-width: 560px) {
          footer > div > div:first-child {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          footer { padding-left: 24px !important; padding-right: 24px !important; }
        }
      `}</style>
    </footer>
  );
}
